/** Pagina "Mas" como utilidades de cuenta y ajustes (design.md §22.1). Sin duplicados operativos. */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileCard } from '@/components/MobileCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { pushService, type PushState } from '@/services/pushService';
import type { MobileIconName } from '@/components/MobileIcon';
import packageJson from '../../../package.json';

type UtilityLink = {
  key: string;
  to: string;
  label: string;
  description: string;
  icon: MobileIconName;
  iconColor: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  permissionCode?: string;
};

/** Enlaces de cuenta: visibles siempre para la sesion activa, salvo permiso indicado. */
const ACCOUNT_LINKS: UtilityLink[] = [
  {
    key: 'profile',
    to: '/m/profile',
    label: 'Mi perfil',
    description: 'Nombre, correo y empresa activa',
    icon: 'person',
    iconColor: 'primary',
  },
];

const APP_NAME = 'ERPA Móvil';
const APP_VERSION = typeof packageJson.version === 'string' ? packageJson.version : '';

type PushUiState = PushState | 'checking';

export function MorePage() {
  const { hasPermission, membershipsCount, logout } = useMobileAuth();
  const [pushStatus, setPushStatus] = useState<PushUiState>(() =>
    pushService.supported() ? 'checking' : 'unsupported',
  );
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [pushError, setPushError] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    if (!pushService.supported()) return;
    let active = true;
    void pushService
      .state()
      .then((state) => {
        if (active) setPushStatus(state);
      })
      .catch(() => {
        if (active) setPushStatus('inactive');
      });
    return () => {
      active = false;
    };
  }, []);

  const visibleLinks = ACCOUNT_LINKS.filter(
    (item) => !item.permissionCode || hasPermission(item.permissionCode),
  );
  const showSwitch = membershipsCount > 1;
  const pushSupported = pushStatus !== 'unsupported';
  const pushActive = pushStatus === 'subscribed';
  const pushCanToggle =
    !pushBusy && (pushStatus === 'inactive' || pushStatus === 'subscribed');

  async function togglePush(): Promise<void> {
    if (!pushCanToggle) return;
    setPushBusy(true);
    setPushMessage(null);
    setPushError(false);
    try {
      if (pushActive) {
        await pushService.unsubscribeCurrent();
        setPushStatus('inactive');
        setPushMessage('Avisos desactivados correctamente');
      } else {
        await pushService.subscribe();
        setPushStatus('subscribed');
        setPushMessage('Avisos activados correctamente');
      }
    } catch (error) {
      setPushMessage(error instanceof Error ? error.message : 'No se pudo actualizar');
      setPushError(true);
      try {
        setPushStatus(await pushService.state());
      } catch {
        setPushStatus(pushActive ? 'subscribed' : 'inactive');
      }
    } finally {
      setPushBusy(false);
    }
  }

  function pushTitle(): string {
    if (pushActive) return 'Desactivar avisos de tareas';
    if (pushStatus === 'denied') return 'Avisos de tareas';
    if (pushStatus === 'checking') return 'Avisos de tareas';
    return 'Activar avisos de tareas';
  }

  function pushDescription(): string {
    if (pushBusy) return pushActive ? 'Desactivando avisos.' : 'Activando avisos.';
    if (pushMessage) return pushMessage;
    if (pushStatus === 'subscribed') return 'Avisos activados en este dispositivo.';
    if (pushStatus === 'denied') return 'Permiso denegado en el navegador.';
    if (pushStatus === 'checking') return 'Revisando estado.';
    return 'Recibe un aviso cuando tengas una tarea nueva';
  }

  return (
    <MobileLayout title="Más" subtitle="Cuenta y ajustes">
      <div className="space-y-3">
        {visibleLinks.map((item) => (
          <Link key={item.key} to={item.to} className="block">
            <MobileCard
              variant="interactive"
              title={item.label}
              description={item.description}
              icon={item.icon}
              iconColor={item.iconColor}
            />
          </Link>
        ))}

        {showSwitch && (
          <Link to="/m/company" className="block">
            <MobileCard
              variant="interactive"
              title="Cambiar de empresa"
              description="Elige la empresa activa"
              icon="swap_horiz"
              iconColor="info"
            />
          </Link>
        )}

        {pushSupported && (
          <MobileCard
            variant={pushCanToggle ? 'interactive' : 'default'}
            title={pushTitle()}
            description={pushDescription()}
            icon={
              pushError
                ? 'error'
                : pushActive
                  ? 'check_circle'
                  : 'notifications_active'
            }
            iconColor={
              pushError ? 'error' : pushActive ? 'success' : 'warning'
            }
            onClick={pushCanToggle ? () => void togglePush() : undefined}
          />
        )}

        <MobileCard
          variant="default"
          title="Acerca de"
          description={APP_VERSION ? `${APP_NAME} · versión ${APP_VERSION}` : APP_NAME}
          icon="info"
          iconColor="info"
        />

        <MobileCard
          variant="interactive"
          title="Cerrar sesión"
          description="Salir de la aplicación en este dispositivo"
          icon="logout"
          iconColor="error"
          onClick={() => setConfirmLogout(true)}
        />
      </div>

      {confirmLogout && (
        <ConfirmDialog
          title="¿Cerrar sesión?"
          message="Se cierra la sesión actual en este dispositivo."
          confirmLabel="Cerrar sesión"
          onConfirm={() => void logout()}
          onCancel={() => setConfirmLogout(false)}
        />
      )}
    </MobileLayout>
  );
}
